import Layout from '@src/components/Layout';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityLogsQuery } from '~/QueryHooks/ActivityLogsQuery';
import { useDebounce } from 'use-lodash-debounce'
import { Dropdown, Input } from 'semantic-ui-react';
import i18n from '~/components/i18n';
import TableComponent from '~/components/TableComponent';
import { DateInput } from 'semantic-ui-calendar-react';
import { useAuth } from '~/routes/components/AuthProvider';
import { useParams } from 'react-router-dom';
import { FARMER_MANAGER_ROLE_SLUG, LIGHT_USER_ROLE_SLUG } from '~/Constants';
import styled from 'styled-components';
import ExcelExport from '~/components/ExcelExport';
import { FarmersService } from '~/api/services/farmers/index';
import { handleErrors } from '~/utils/helpers';

const FiltersWrapper = styled.div`
display: flex;
align-items: baseline;
flex-wrap: wrap;
gap: 10px;
`

const getTableColumnHeaders = () => [
  {
    name: i18n.t('Date'),
    key: 'createdAt',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm'
  },
  {
    name: i18n.t('User'),
    key: 'name'
  },
  {
    name: i18n.t('Supplier No.'),
    key: 'supplierNo'
  },
  {
    name: i18n.t('Role'),
    key: 'roleLabel'
  },
  {
    name: i18n.t('Action'),
    key: 'label'
  },
  {
    name: i18n.t('Browser'),
    key: 'browser',
    type: 'browser'
  },
  {
    name: i18n.t('Device'),
    key: 'device',
    type: 'device'
  }
]

const ActivityLogs: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 10,
    search: '',
    type: '',
    action: '',
    dateFrom: '',
    dateTo: ''
  })
  const [search, setSearch] = useState('')
  const [activityLogData, setActivityLogData] = useState(null)
  const [isBusy, setIsBusy] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const debouncedValue = useDebounce(search, 300)
  const { systemData: { languages, logged: { languageId, roleSlug } } } = useAuth()
  const languageLocal = languages.find(language => language.id === languageId)
  const lightUserLogged = (roleSlug === LIGHT_USER_ROLE_SLUG) || (roleSlug === FARMER_MANAGER_ROLE_SLUG)
  const { id } = useParams()

  const {
    data: activityLogOptions,
    isLoading: isBusyActivityLogOptions
  } = ActivityLogsQuery.getActivityLogsOptions({ farmer: 1 })

  useEffect(() => {
    fetchData()
  }, [dataFiltered])

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedValue })
  }, [debouncedValue])

  async function fetchData() {
    setIsFetching(true)
    try {
      const response = await FarmersService.getManagerActivityLogs(id, {
        ...dataFiltered,
        page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1,
        dateFrom: dataFiltered.dateFrom !== '' ? parseDate(dataFiltered.dateFrom) : '',
        dateTo: dataFiltered.dateTo !== '' ? parseDate(dataFiltered.dateTo) : ''
      })
      setActivityLogData(response.data)
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsFetching(false)
    }
  }

  function parseDate(value) {
    const arrayOfValues = value.split('.')
    return `${arrayOfValues[2]}-${arrayOfValues[1]}-${arrayOfValues[0]}`
  }

  const handleKeyPress = (event) => { event.preventDefault() }

  function renderActions() {
    return <ExcelExport
      url={`farmers/${id}/activity-logs/export`}
      fileName={t('activity logs')}
      data={{
        ...dataFiltered,
        page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1,
        dateFrom: dataFiltered.dateFrom !== '' ? parseDate(dataFiltered.dateFrom) : '',
        dateTo: dataFiltered.dateTo !== '' ? parseDate(dataFiltered.dateTo) : ''
      }}
    />
  }

  function renderFilters() {
    return <FiltersWrapper>
      <Input
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Search...')}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
      <Dropdown
        placeholder={t('Select activity action')}
        options={activityLogOptions?.map(activityLog => ({ value: activityLog.key, text: activityLog.label })) || []}
        onChange={(e, data) => setDataFiltered({ ...dataFiltered, action: data.value as string })}
        clearable
        selection
        search
        selectOnBlur={false}
      />
      <DateInput
        name='date'
        autoComplete='off'
        placeholder={t('Date from')}
        closable
        clearIcon={'close'}
        onKeyPress={handleKeyPress}
        clearable
        localization={languageLocal.code}
        closeOnMouseLeave={false}
        dateFormat={'DD.MM.YYYY'}
        className={'datePicker'}
        value={dataFiltered.dateFrom}
        iconPosition='left'
        onChange={(event, { value }) => setDataFiltered({ ...dataFiltered, dateFrom: value })}
      />
      <DateInput
        name='date'
        autoComplete='off'
        placeholder={t('Date to')}
        closable
        clearIcon={'close'}
        clearable
        localization={languageLocal.code}
        onKeyPress={handleKeyPress}
        closeOnMouseLeave={false}
        dateFormat={'DD.MM.YYYY'}
        className={'datePicker'}
        minDate={dataFiltered.dateFrom}
        value={dataFiltered.dateTo}
        iconPosition='left'
        onChange={(event, { value }) => setDataFiltered({ ...dataFiltered, dateTo: value })}
      />
    </FiltersWrapper>
  }

  const { filtered, rows } = activityLogData ? activityLogData : { filtered: 0, rows: [] }

  return <Layout
    childContainerSize={'auto'}
    isBusy={isBusy || isBusyActivityLogOptions}
    actions={!lightUserLogged && renderActions()}
    filters={renderFilters()}
    useSpinner
    title={''}>
    <TableComponent
      animated
      isFetchingData={isFetching}
      tableColumnHeaders={getTableColumnHeaders()}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data) => setDataFiltered(data)}
      total={filtered}
      rows={rows}
    />
  </Layout>;
};

export default ActivityLogs;
